import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { IonChip, IonIcon } from '@ionic/react';
import { checkmarkCircle, closeCircle } from 'ionicons/icons';
import { ContactLogic } from '@/modules/contact';
import { useContact } from '@/modules/contact/useContact';
import { pxToRem } from '@/modules/config/constants';
import type { MessageIntent } from '@/modules/template';
import { INTENT_SYMBOL } from './logic';

// When the recipient list is expanded, cap it to a few rows and scroll rather
// than letting it grow to fill (and overrun) the screen. A row is ~36px — a
// ~28px chip plus the 6px flex gap, with a little slack.
const EXPANDED_VISIBLE_ROWS = 4;
const EXPANDED_ROW_HEIGHT_PX = 36;

interface RecipientBoxProps {
  value: string[];
  onRemove?: (index: number) => void;
  contactIntents?: Map<string, MessageIntent>;
  introBlockedKeys?: Set<string>;
  confirmedContactIds?: ReadonlySet<string>;
  to?: React.ReactNode;
  isExpandedOnMount?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  disabled?: boolean;
  readOnly?: boolean;
  collapsedRows?: number;
  expandable?: boolean;
  sort?: (a: string, b: string) => number;
  // When set, chips become clickable — e.g. to preview a singleton message
  // personalized for that recipient. Deliberately no selected-state styling:
  // it's an incidental peek, not a required choice.
  onSelectContact?: (localContactId: string) => void;
}

interface ContactChipProps {
  localContactId: string;
  index: number;
  contactIntents?: Map<string, MessageIntent>;
  introBlockedKeys?: Set<string>;
  confirmed?: boolean;
  onRemove?: (index: number) => void;
  disabled?: boolean;
  readOnly?: boolean;
  onSelect?: () => void;
}

const ContactChip: React.FC<ContactChipProps> = ({
  localContactId,
  index,
  contactIntents,
  introBlockedKeys,
  confirmed = false,
  onRemove,
  disabled,
  readOnly,
  onSelect,
}) => {
  const { contact } = useContact(localContactId);
  if (!contact) return null;

  const displayName = ContactLogic.displayName(contact);
  const isIntroBlocked = introBlockedKeys?.has(localContactId) ?? false;
  const intent = contactIntents?.get(localContactId);

  return (
    <IonChip
      key={`chip-${index}`}
      data-testid={`recipient-chip-${localContactId}`}
      onClick={onSelect}
      style={
        {
          fontSize: pxToRem(14),
          minHeight: '28px',
          height: 'auto',
          margin: 0,
          paddingRight: readOnly || confirmed ? 14 : 4,
          paddingLeft: readOnly || confirmed ? 14 : undefined,
          cursor: onSelect && !disabled ? 'pointer' : 'default',
          '--background': '#ffffff',
        } as React.CSSProperties
      }
    >
      <span
        style={{
          lineHeight: pxToRem(18),
          color: confirmed ? '#999' : undefined,
          textDecoration: isIntroBlocked ? 'line-through' : undefined,
        }}
      >
        {displayName}
        {!confirmed && !isIntroBlocked && intent
          ? ` ${INTENT_SYMBOL[intent]}`
          : null}
      </span>
      {confirmed && (
        <IonIcon
          icon={checkmarkCircle}
          aria-hidden="true"
          style={{ fontSize: pxToRem(16), marginLeft: 4, color: '#4caf50' }}
        />
      )}
      {!readOnly && !confirmed && onRemove && (
        <button
          type="button"
          aria-label={`Remove ${displayName}`}
          disabled={disabled}
          onClick={event => {
            event.stopPropagation();
            if (!disabled) onRemove(index);
          }}
          style={{
            border: 0,
            background: 'transparent',
            color: 'currentColor',
            width: 24,
            height: 24,
            padding: 0,
            marginLeft: 2,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
          }}
        >
          <IonIcon
            icon={closeCircle}
            aria-hidden="true"
            style={{ fontSize: pxToRem(18) }}
          />
        </button>
      )}
    </IonChip>
  );
};

const RecipientBox: React.FC<RecipientBoxProps> = ({
  value: inputContactIds,
  onRemove,
  contactIntents,
  introBlockedKeys,
  confirmedContactIds,
  to = 'To',
  isExpandedOnMount = false,
  onExpandedChange,
  disabled = false,
  readOnly = false,
  collapsedRows,
  expandable = true,
  sort,
  onSelectContact,
}) => {
  const orderedContactIds = sort
    ? [...inputContactIds].sort(sort)
    : inputContactIds;
  const [isRecipientListExpanded, setIsRecipientListExpanded] =
    useState<boolean>(isExpandedOnMount);
  const [rowLimitedVisible, setRowLimitedVisible] = useState<{
    key: string;
    count: number;
  } | null>(null);
  const recipientListRef = useRef<HTMLDivElement>(null);
  const recipientAreaRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const selectedContactKey = orderedContactIds.join('\u0000');

  const expandRecipientList = useCallback(() => {
    if (disabled || !expandable) return;
    setIsRecipientListExpanded(true);
  }, [disabled, expandable]);

  const collapseRecipientList = useCallback(() => {
    setIsRecipientListExpanded(false);
  }, []);

  useEffect(() => {
    onExpandedChange?.(isRecipientListExpanded);
  }, [isRecipientListExpanded, onExpandedChange]);

  // Track the parent-controlled expand state in both directions so the parent
  // can collapse the box after mount — e.g. once the update flow finishes
  // prepopulating a long list of recipients — not just expand it.
  useEffect(() => {
    setIsRecipientListExpanded(isExpandedOnMount);
  }, [isExpandedOnMount]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isRecipientListExpanded &&
        recipientListRef.current &&
        !recipientListRef.current.contains(event.target as Node)
      ) {
        setIsRecipientListExpanded(false);
      }
    };

    if (isRecipientListExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isRecipientListExpanded]);

  useLayoutEffect(() => {
    if (!collapsedRows || isRecipientListExpanded) {
      setRowLimitedVisible(null);
      return;
    }

    const measureVisibleRows = () => {
      const rowTops = new Set<number>();
      let visibleCount = orderedContactIds.length;

      for (let index = 0; index < orderedContactIds.length; index += 1) {
        const chip = chipRefs.current[index];
        if (!chip) continue;

        rowTops.add(chip.offsetTop);
        if (rowTops.size > collapsedRows) {
          visibleCount = index;
          break;
        }
      }

      const hiddenCount = orderedContactIds.length - visibleCount;
      // Leave room for the "+n" chip when a row is full.
      setRowLimitedVisible({
        key: selectedContactKey,
        count: hiddenCount > 0 ? Math.max(0, visibleCount - 1) : visibleCount,
      });
    };

    measureVisibleRows();

    // Re-measure once the container is properly sized. During an Ionic page
    // enter animation the layout effect can fire before the router outlet has
    // given the page its final width, causing all chips to share offsetTop 0
    // and the row limit to be skipped. The observer fires one more time when
    // the container settles; we only re-measure while all chips are still
    // rendered (prevents oscillation after a successful trim).
    const container = recipientAreaRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => {
      const allChipsRendered =
        orderedContactIds.length === 0 ||
        chipRefs.current[orderedContactIds.length - 1] != null;
      observer.disconnect();
      if (allChipsRendered) measureVisibleRows();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [
    collapsedRows,
    isRecipientListExpanded,
    orderedContactIds.length,
    selectedContactKey,
  ]);

  const rowLimitedVisibleCount =
    rowLimitedVisible?.key === selectedContactKey
      ? rowLimitedVisible.count
      : null;

  const visibleContactIds = isRecipientListExpanded
    ? orderedContactIds
    : collapsedRows
      ? orderedContactIds.slice(
          0,
          rowLimitedVisibleCount ?? orderedContactIds.length
        )
      : orderedContactIds.slice(0, 2);
  const hiddenContactCount =
    orderedContactIds.length - visibleContactIds.length;

  return (
    <div
      ref={recipientListRef}
      data-recipient-expanded={isRecipientListExpanded}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        position: 'relative',
        minHeight: 0,
      }}
    >
      {to != null && to !== '' && (
        <span
          // When expanded, tapping the "To" label collapses the box — a
          // discoverable target that doesn't require hunting for empty space to
          // click outside of.
          role={isRecipientListExpanded ? 'button' : undefined}
          tabIndex={isRecipientListExpanded ? 0 : undefined}
          aria-label={
            isRecipientListExpanded ? 'Collapse recipients' : undefined
          }
          onClick={isRecipientListExpanded ? collapseRecipientList : undefined}
          onKeyDown={
            isRecipientListExpanded
              ? event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    collapseRecipientList();
                  }
                }
              : undefined
          }
          style={{
            color: '#999',
            fontSize: pxToRem(14),
            lineHeight: pxToRem(28),
            paddingTop: readOnly ? '6px' : '9px',
            flexShrink: 0,
            cursor: isRecipientListExpanded ? 'pointer' : 'default',
          }}
        >
          {to}
        </span>
      )}
      <div
        ref={recipientAreaRef}
        style={{
          cursor: disabled ? 'not-allowed' : 'default',
          // One resting height in both states. Expanding is about how many rows
          // are shown, not how tall an empty (or single-row) box is, so a
          // height that tracked the expanded flag only produced a jump: the box
          // starts expanded, and the first mousedown outside it — clicking the
          // "exclude" checkbox right below, say — collapses it and yanks
          // everything under it up a few pixels.
          minHeight: '36px',
          // Cap the expanded list to a few rows and scroll — otherwise a long
          // recipient list (e.g. update-flow prepopulation) grows to fill the
          // whole screen. flex-start avoids clipping the top rows once the
          // flex container scrolls.
          maxHeight: isRecipientListExpanded
            ? EXPANDED_VISIBLE_ROWS * EXPANDED_ROW_HEIGHT_PX
            : undefined,
          overflowY: isRecipientListExpanded ? 'auto' : 'visible',
          display: 'flex',
          alignItems: isRecipientListExpanded ? 'flex-start' : 'center',
          flexWrap: 'wrap',
          gap: '6px',
          paddingTop: '6px',
          paddingRight: '6px',
          paddingBottom: '6px',
          paddingLeft: isRecipientListExpanded ? '6px' : '0',
          opacity: disabled ? 0.6 : 1,
          flex: 1,
          minWidth: 0,
        }}
        role="group"
        aria-label="Recipients"
      >
        {visibleContactIds.map((contactId, index) => (
          <span
            key={`chip-${contactId}-${index}`}
            ref={el => {
              chipRefs.current[index] = el;
            }}
            style={{ display: 'inline-flex' }}
          >
            <ContactChip
              localContactId={contactId}
              index={index}
              contactIntents={contactIntents}
              introBlockedKeys={introBlockedKeys}
              confirmed={confirmedContactIds?.has(contactId)}
              onRemove={onRemove}
              disabled={disabled}
              readOnly={readOnly}
              onSelect={
                onSelectContact ? () => onSelectContact(contactId) : undefined
              }
            />
          </span>
        ))}
        {!isRecipientListExpanded && hiddenContactCount > 0 && expandable && (
          <button
            type="button"
            aria-label={`Show all ${orderedContactIds.length} recipients`}
            disabled={disabled}
            onClick={event => {
              event.stopPropagation();
              expandRecipientList();
            }}
            style={{
              border: 0,
              fontSize: pxToRem(12),
              height: pxToRem(24),
              margin: 0,
              padding: '0 10px',
              borderRadius: 12,
              backgroundColor: '#e0e0e0',
              color: '#666',
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            +{hiddenContactCount}
          </button>
        )}
        {!isRecipientListExpanded && hiddenContactCount > 0 && !expandable && (
          <span
            style={{
              border: 0,
              fontSize: pxToRem(12),
              height: pxToRem(24),
              margin: 0,
              padding: '0 10px',
              borderRadius: 12,
              backgroundColor: '#e0e0e0',
              color: '#666',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            +{hiddenContactCount}
          </span>
        )}
      </div>
    </div>
  );
};

export default RecipientBox;
